import { useMetadata } from '../contexts/MetadataContext';
import { ListingResource } from '../system/field';

/**
 * Converts a snake_case string to PascalCase
 */
export const snakeToPascalCase = (str: string): string => {
  // Handle strings that are already PascalCase or camelCase
  if (!/[_\s]/.test(str) && str !== str.toUpperCase()) {
    return str;
  }

  // Handle uppercase snake case (LIKE_THIS -> LikeThis)
  return str
    .toLowerCase()
    .split(/[_\s]/)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
};

/**
 * Converts a PascalCase string to SNAKE_CASE
 */
export const pascalToSnakeCase = (str: string): string => {
  // Handle strings that are already SNAKE_CASE
  if (str === str.toUpperCase() && str.includes('_')) {
    return str;
  }

  // Convert PascalCase or camelCase to SNAKE_CASE
  const result = str.replace(/([A-Z])/g, '_$1').toUpperCase();
  return result.startsWith('_') ? result.substring(1) : result;
};

/**
 * Service to translate between application field names and RESO API field names
 */
export class FieldTranslator {
  private resourceType: ListingResource | string;
  private translation: Record<string, string>;
  private reverseTranslation: Record<string, string>;

  constructor(
    resourceType: ListingResource | string,
    translation: Record<string, string>,
  ) {
    this.resourceType = resourceType;
    this.translation = translation;

    // Create reverse translation map (RESO to app)
    this.reverseTranslation = Object.entries(translation).reduce(
      (acc, [appField, resoField]) => {
        acc[resoField] = appField;
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  /**
   * Convert an application field name to RESO API field name
   */
  toResoField(appField: string): string {
    // If there's a translation, use it
    if (this.translation[appField]) {
      return this.translation[appField];
    }

    // Default handling: Convert app field (SNAKE_CASE) to RESO field (PascalCase)
    return snakeToPascalCase(appField);
  }

  /**
   * Convert a RESO API field name to application field name
   */
  toAppField(resoField: string): string {
    // If there's a reverse translation, use it
    if (this.reverseTranslation[resoField]) {
      return this.reverseTranslation[resoField];
    }

    // Default handling: Convert RESO field (PascalCase) to app field (SNAKE_CASE)
    return pascalToSnakeCase(resoField);
  }

  /**
   * Translate an entire object from application format to RESO API format
   */
  translateToReso(appObject: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    for (const [appField, value] of Object.entries(appObject)) {
      // Skip undefined values
      if (value === undefined) continue;

      const resoField = this.toResoField(appField);
      result[resoField] = value;
    }

    return result;
  }

  /**
   * Translate an entire object from RESO API format to application format
   */
  translateToApp(resoObject: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    for (const [resoField, value] of Object.entries(resoObject)) {
      // Skip undefined values
      if (value === undefined) continue;
      // Skip META fields
      if (resoField.startsWith('@')) continue;

      const appField = this.toAppField(resoField);
      result[appField] = value;
    }

    return result;
  }
}

/**
 * Factory function to create a field translator for a specific resource type
 */
export const createFieldTranslator = (
  resourceType: ListingResource | string,
  metadataTranslation: any,
): FieldTranslator => {
  // For listing resources, use the translation from the corresponding section
  if (
    Object.values(ListingResource).includes(resourceType as ListingResource)
  ) {
    const translation =
      metadataTranslation?.[resourceType as ListingResource] || {};
    return new FieldTranslator(resourceType, translation);
  }

  // For external resources, may need specific handling or return empty translation
  return new FieldTranslator(resourceType, {});
};

/**
 * Hook to get a field translator for a specific resource type
 */
export const useFieldTranslator = (
  resourceType: ListingResource | string,
): FieldTranslator => {
  const { locale } = useMetadata();
  const translation = locale();

  return createFieldTranslator(resourceType, translation);
};
